/**
 * La sucursal de este aparato: la del selector de la izquierda.
 *
 * Desde el 2026-09-27 cada sucursal tiene su catálogo, y desde el 2026-09-28
 * sus estadísticas: lo que se ve en Catálogo, Inventario, Reportes o Inicio es
 * de la sucursal elegida ahí. Esto la lee y la enseña.
 */

import { useCallback } from "react";
import { create } from "zustand";
import { useQuery, useQueryClient, type Query } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Store, Warehouse } from "lucide-react";
import { Insignia, Nota } from "@/components/ui";
import { Confirmar } from "@/components/Modal";
import { negocio } from "@/lib/endpoints";
import { plural } from "@/lib/texto";
import { useAuth } from "@/stores/auth";
import { useCarrito } from "@/stores/carrito";
import { useSucursal } from "@/stores/sucursal";
import type { Location } from "@/lib/types";

/**
 * Lo que es del negocio entero y no de una sucursal: sus ajustes, la lista de
 * sucursales, la suscripción, las monedas, los avisos. Al cambiar de sucursal
 * solo se vuelve a pedir; vaciarlo haría parpadear la barra y el menú.
 */
const DEL_NEGOCIO_ENTERO = new Set([
  "negocio",
  "sucursales",
  "suscripcion-estado",
  "monedas-negocio",
  "avisos",
]);

type Destino = { id: string; nombre: string };

/** El cambio pedido con una venta en curso, esperando respuesta. */
const usePregunta = create<{
  pendiente: { destino: Destino; desde: string | null; despues?: () => void } | null;
}>(() => ({ pendiente: null }));

/**
 * Cambiar la sucursal de este aparato.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ NO BASTA CON GUARDARLA (2026-09-29)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El dueño lo reportó: al cambiar de sucursal estando en Catálogo, Vender o
 * Inventario, la pantalla no cambiaba. Tres cosas se quedaban de la otra:
 *
 *   · Los datos en pantalla. Antes se marcaban como viejos y se volvían a
 *     pedir, pero MIENTRAS llegaban se seguía viendo lo de la otra sucursal con
 *     el nombre de la nueva arriba; un producto editado en ese momento era la
 *     copia de la otra. Ahora se vacían: la pantalla carga, y lo que aparece
 *     es de la nueva.
 *   · La pantalla misma: una ventana abierta, un filtro, la página 4. Se abre
 *     de cero (ver la `key` del contenido en `AppLayout`).
 *   · El carrito, con los productos y los precios de la otra. Se vacía, y por
 *     eso antes se pregunta si hay una venta en curso
 *     (`PreguntaDeCambioDeSucursal`).
 *
 * La usan el selector de la izquierda y «Entrar» de «Todas las sucursales»:
 * una sola, para que todos hagan lo mismo.
 *
 *   · `cambiar`: lo normal. En la misma sucursal no hace nada; con una venta
 *     en curso, pregunta. `despues` es lo que sigue (ir a su Inicio, avisar).
 *   · `aplicar`: sin preguntar. Solo para corregir una sucursal guardada que
 *     ya no le toca a quien entra: su venta tampoco se podría cobrar.
 */
export function useCambiarDeSucursal() {
  const tenantId = useAuth((s) => s.tenant?.id);
  const poner = useSucursal((s) => s.poner);
  const qc = useQueryClient();
  const { actual } = useSucursales();

  const aplicar = useCallback(
    (locationId: string) => {
      if (!tenantId) return;
      // Primero la sucursal: todo lo que se vuelva a pedir ya sale con la nueva.
      poner(tenantId, locationId);
      useCarrito.getState().vaciar();
      const delNegocio = (q: Query) => DEL_NEGOCIO_ENTERO.has(String(q.queryKey[0]));
      void qc.resetQueries({ predicate: (q) => !delNegocio(q) });
      void qc.invalidateQueries({ predicate: delNegocio });
    },
    [tenantId, poner, qc],
  );

  const actualId = actual?.id;
  const actualNombre = actual?.name ?? null;
  const cambiar = useCallback(
    (destino: Destino, despues?: () => void) => {
      if (destino.id === actualId) {
        despues?.();
        return;
      }
      if (useCarrito.getState().lineas.length > 0) {
        usePregunta.setState({ pendiente: { destino, desde: actualNombre, despues } });
        return;
      }
      aplicar(destino.id);
      despues?.();
    },
    [actualId, actualNombre, aplicar],
  );

  return { cambiar, aplicar };
}

/**
 * «¿Cambiar de sucursal?» cuando hay una venta en curso.
 *
 * Va UNA vez, en `AppLayout`, y no dentro de cada botón que cambia de
 * sucursal: así pregunta igual venga el cambio de donde venga, y la ventana no
 * queda encerrada dentro de la barra lateral.
 */
export function PreguntaDeCambioDeSucursal() {
  const pendiente = usePregunta((s) => s.pendiente);
  const lineas = useCarrito((s) => s.lineas.length);
  const { aplicar } = useCambiarDeSucursal();
  const cerrar = () => usePregunta.setState({ pendiente: null });

  return (
    <Confirmar
      abierto={pendiente !== null}
      onCerrar={cerrar}
      onConfirmar={() => {
        if (!pendiente) return;
        cerrar();
        aplicar(pendiente.destino.id);
        pendiente.despues?.();
      }}
      titulo="¿Cambiar de sucursal?"
      mensaje={
        `La venta en curso tiene ${plural(lineas, "producto", "productos")}. ` +
        `Al pasar a ${pendiente?.destino.nombre ?? "otra sucursal"} se vaciará, porque los ` +
        "precios y la existencia son propios de cada sucursal.\n\n" +
        `Si deseas completarla primero, cancela y cóbrala en ${pendiente?.desde ?? "esta sucursal"}.`
      }
      textoConfirmar="Cambiar y vaciar la venta"
      peligroso
    />
  );
}

/**
 * La sucursal de este aparato (o la principal) y las demás abiertas.
 *
 * `varias` es falso con una sola: ahí no se habla de sucursales.
 */
export function useSucursales() {
  const { tenant } = useAuth();
  const elegida = useSucursal((s) => s.porNegocio[tenant?.id ?? ""] ?? null);
  const { data } = useQuery({
    queryKey: ["sucursales", tenant?.id],
    queryFn: negocio.sucursales,
    enabled: Boolean(tenant?.id),
    staleTime: 5 * 60 * 1000,
  });
  const abiertas = (data ?? []).filter((l) => l.is_active);
  // La lista trae solo las de quien entra (2026-09-29): puede no estar la
  // principal. `SelectorNegocio` corrige lo guardado en el aparato.
  const actual =
    abiertas.find((l) => l.id === elegida) ??
    abiertas.find((l) => l.is_default) ??
    abiertas[0] ??
    null;
  return { actual, abiertas, varias: abiertas.length > 1 };
}

/**
 * Las sucursales que ya reciben solas los PRECIOS de `actual` (2026-10-07):
 * las de su catálogo sincronizado con «mismos precios en todas». A esas no se
 * les pregunta «¿Aplicar también en las otras sucursales?» al cambiar un
 * precio: ya lo tienen. Ver `catalog/sincronizados.py`.
 */
export function conLosMismosPrecios(actual: Location | null, sucursales: Location[]): Set<string> {
  if (!actual?.catalogo_grupo || actual.catalogo_precios_iguales === false) return new Set();
  return new Set(
    sucursales.filter((l) => l.id !== actual.id && l.catalogo_grupo === actual.catalogo_grupo).map((l) => l.id),
  );
}

/**
 * ¿Se está trabajando en un almacén? (2026-10-01) Ahí no se vende ni se maneja
 * dinero, y el catálogo es solo de productos y combos.
 */
export function useEnUnAlmacen(): boolean {
  return useSucursales().actual?.tipo === "almacen";
}

/**
 * De qué sucursal es lo que se ve, al lado del título: Catálogo, Precios,
 * Inventario, Reportes, Estadísticas, Utilidad, Inicio. Solo con varias: con
 * una, sería ruido.
 */
export function DeLaSucursal() {
  const { actual, varias } = useSucursales();
  if (!varias || !actual) return null;
  return (
    <Insignia tono="acento">
      {actual.tipo === "almacen" ? (
        <Warehouse size={12} aria-hidden="true" />
      ) : (
        <Store size={12} aria-hidden="true" />
      )}{" "}
      {actual.name}
    </Insignia>
  );
}

/**
 * «En Total Paramillo no hubo movimientos en este período.»
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ HACE FALTA DECIRLO (2026-09-29)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Desde el 2026-09-28 cada pantalla de cifras enseña SOLO la sucursal elegida.
 * Una sucursal recién abierta, sin ventas, lo enseña todo en cero, y así se lee
 * como que «no salen los movimientos»: lo reportó el dueño mirando las cuentas
 * de un cliente con tres sucursales y ventas solo en la principal.
 *
 * Con un solo local no se dice nada: ahí cero es cero.
 */
export function SinMovimientosAqui({ que = "movimientos" }: { que?: string }) {
  const { actual, varias } = useSucursales();
  const { puede } = useAuth();
  if (!varias || !actual) return null;
  return (
    <Nota tono="neutro" icono={<Store size={15} />}>
      En <strong>{actual.name}</strong> no hubo {que} en este período. Aquí se ven solo las
      cifras de esta sucursal; las demás tienen las suyas.{" "}
      {puede("reports.sucursales") && (
        <Link to="/sucursales" className="font-semibold text-accent underline">
          Ver todas las sucursales
        </Link>
      )}
    </Nota>
  );
}
